import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import emojiFlags from 'emoji-flags';
import { imageProxy } from 'tf-post-parser';
import { hotelLink } from 'helpers/hotelLink';
import Head from '../Header/Head';
import EditLocationDetails from '../General/EditLocationDetails';
import StartBlogCta from '../Post/Article/StartBlogCta';
import InlineBlogCta from '../Post/Article/InlineBlogCta';
import ChapterChips from './ChapterChips';
import CountryMapPosts from './CountryMapPosts';
import CustomsCard from './CustomsCard';
import BudgetBoard from './BudgetBoard';
import DestinationImage from './DestinationImage';
import DishMenuCard from './DishMenuCard';
import EntryCard from './EntryCard';
import FieldNotesProse from './FieldNotesProse';
import GuideAppStrip from './GuideAppStrip';
import GuideHero from './GuideHero';
import GuideFaq from './GuideFaq';
import GuidePostGrid from './GuidePostGrid';
import RouteItinerary from './RouteItinerary';
import SectionHeading, { SectionDivider } from './SectionHeading';
import SubdivisionGrid from './SubdivisionGrid';
import ThingsToDoList from './ThingsToDoList';
import TravelCalendar from './TravelCalendar';

export default function DestinationTravelGuide({
  locationDetails,
  query,
  noEdit,
  itineraries,
  noIndex,
  breadcrumbs,
}) {
  const generatedOgImage = `https://og.travelfeed.com/api/travelGuide?title=${encodeURIComponent(
    locationDetails.title,
  )}&image=${imageProxy(locationDetails.image, 2000)}`;

  let acceptedAnswerText = '<ul>';
  if (locationDetails.travelTimes && locationDetails.travelTimes.length > 0)
    locationDetails.travelTimes.forEach(({ monthRange, seasonDescription }) => {
      acceptedAnswerText += `<li><strong>${monthRange}</strong> ${seasonDescription}</li>`;
    });
  acceptedAnswerText += '</ul>';
  const whenToGoFaqItem = {
    questionName: 'When to go?',
    acceptedAnswerText,
  };

  // Affiliate destinations — revenue-bearing, keep in sync with GuideFaq's
  // keyword injection and BudgetBoard's line items. Hotels run through the
  // central helper: Booking + Agoda are dead/off-limits and no general-hotel
  // program is approved on Travelpayouts yet, so hotelUrl is null today and the
  // BudgetBoard hotel lines render unlinked. Relight them in helpers/hotelLink.
  const hotelUrl = hotelLink(locationDetails.title);
  let busLink = 'https://omio.tp.st/IOYADJSR';
  if (
    [
      'th',
      'ph',
      'in',
      'id',
      'vn',
      'kh',
      'sg',
      'my',
      'mm',
      'la',
      'jp',
      'lk',
      'nz',
      'cn',
      'hk',
      'au',
      'tr',
      'eg',
      'jo',
      'ma',
      'ge',
    ].indexOf(locationDetails.countryCode) !== -1
  )
    busLink = 'https://12go.tp.st/rioEGvek';
  const rentalCarLink = 'https://discovercars.tp.st/yQKbm54Z';
  const privateDriverLink = 'https://gettransfer.tp.st/6xg0KNBh';
  const taxiLink = 'https://kiwitaxi.tp.st/NnUiV5id';
  const toursLink = `https://www.getyourguide.com/s/?q=${encodeURIComponent(
    locationDetails.title,
  )}&partner_id=0NCPEPQ&utm_medium=online_publisher`;

  // The body can be model-written: keep formatting, links to web pages and
  // images, drop everything that runs (scripts, iframes, event handlers).
  const reactParsed = parse(
    sanitizeHtml(locationDetails.body || '', {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
      allowedSchemes: ['http', 'https'],
    }),
  );

  const hasItineraries = itineraries && itineraries.length > 0;
  const images = locationDetails.images || [];
  const hasSubdivisions =
    ['it', 'fr', 'es', 'us', 'gb'].indexOf(
      locationDetails.countryCode.toLowerCase(),
    ) !== -1;
  const hasEtiquette = Boolean(
    locationDetails.travelEtiquette &&
      ((locationDetails.travelEtiquette.dos &&
        locationDetails.travelEtiquette.dos.length > 0) ||
        (locationDetails.travelEtiquette.donts &&
          locationDetails.travelEtiquette.donts.length > 0)),
  );
  const hasFaq = Boolean(locationDetails.faq && locationDetails.faq.length > 0);

  // The chips under the hero and the numbered eyebrows derive from one
  // list, built from the same conditions that render the sections — a chip
  // can never point at a chapter that doesn't exist.
  const chapters = [
    locationDetails.travelTimes && locationDetails.travelTimes.length > 0
      ? { id: 'when-to-go', label: 'When to go', eyebrow: 'The seasons' }
      : null,
    locationDetails.budget
      ? { id: 'costs', label: 'What it costs', eyebrow: 'Daily fares' }
      : null,
    locationDetails.topThingsToDo && locationDetails.topThingsToDo.length > 0
      ? { id: 'experiences', label: 'Experiences', eyebrow: 'The shortlist' }
      : null,
    locationDetails.mustTryDishes && locationDetails.mustTryDishes.length > 0
      ? { id: 'food', label: 'At the table', eyebrow: 'At the table' }
      : null,
    hasItineraries
      ? { id: 'itineraries', label: 'Itineraries', eyebrow: 'The flight plan' }
      : null,
    hasSubdivisions
      ? { id: 'regions', label: 'Regions', eyebrow: 'Go deeper' }
      : null,
    { id: 'stories', label: 'Stories', eyebrow: 'The community' },
  ].filter(Boolean);
  // "01 · The seasons" — chapter number as it appears in the chips.
  const chapterEyebrow = id => {
    const index = chapters.findIndex(chapter => chapter.id === id);
    return `${String(index + 1).padStart(2, '0')} · ${chapters[index].eyebrow}`;
  };

  return (
    <>
      <Head
        shorttitle={`${
          emojiFlags.countryCode(locationDetails.countryCode.toUpperCase())
            .emoji
        } ${locationDetails.title} Travel Guide (${new Date().getFullYear()})`}
        description={`The ultimate travel guide to ${locationDetails.title}: Your all-in-one travel resource for insider tips, itineraries, must-sees and hidden gems in ${locationDetails.title}.`}
        faq={[whenToGoFaqItem, ...locationDetails.faq]}
        image={generatedOgImage}
        includeMapbox
        noIndex={noIndex}
        breadcrumbs={breadcrumbs}
        // the catch-all route serves guides under URL variants (raw-space
        // paths, query strings) — canonicalize country guides to the slug
        seo={
          query.subdivision
            ? undefined
            : {
                canonicalUrl: `https://travelfeed.com/destinations/${query.country}`,
              }
        }
      />

      <GuideHero
        image={locationDetails.image}
        blur={locationDetails.blur}
        alt={locationDetails.coverAlt || locationDetails.title}
        title={locationDetails.title}
        kicker={`Field guide · ${new Date().getFullYear()} edition`}
        breadcrumbs={[{ name: 'Travel Guides', href: '/destinations' }]}
        countryCode={locationDetails.countryCode}
        bbox={locationDetails.bbox}
        capitalCity={locationDetails.capitalCity}
        locationDetails={locationDetails}
        editButton={
          !noEdit && (
            <EditLocationDetails
              countryCode={locationDetails.countryCode}
              data={locationDetails}
              triggerClassName="tw-text-[rgba(255,255,255,0.6)] hover:tw-text-white"
            />
          )
        }
      />

      <ChapterChips chapters={chapters} />

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4">
        {/* Know before you go: the country's story, with the entry card
            laid on the desk beside it — slightly tilted, like a passport
            dropped on the page. */}
        <section className="tw-py-14 sm:tw-py-20">
          <SectionHeading eyebrow="First impressions">
            Know before you go.
          </SectionHeading>
          <div className="tw-grid tw-grid-cols-1 tw-items-start tw-gap-10 lg:tw-grid-cols-[1fr_minmax(0,24rem)]">
            <div className="tw-order-2 lg:tw-order-1">
              <FieldNotesProse dropCap>{reactParsed}</FieldNotesProse>
            </div>
            <div className="tw-order-1 lg:tw-order-2 lg:-tw-mt-6">
              <div className="-tw-rotate-[1.2deg] tw-transition-transform hover:tw-rotate-0">
                <EntryCard locationDetails={locationDetails} />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* When to go — a full-width tinted band breaks the single-column
          card rhythm; the calendar strip gets its own stage. */}
      {locationDetails.travelTimes && locationDetails.travelTimes.length > 0 && (
        <section
          id="when-to-go"
          className="tw-scroll-mt-20 tw-bg-[#F3EFE4] dark:tw-bg-[#13211B]"
        >
          <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-py-14 sm:tw-py-20">
            <SectionHeading eyebrow={chapterEyebrow('when-to-go')}>
              When to go.
            </SectionHeading>
            <TravelCalendar travelTimes={locationDetails.travelTimes} />
          </div>
        </section>
      )}

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pt-14 sm:tw-pt-20">
        {/* What it costs */}
        {locationDetails.budget && (
          <section id="costs" className="tw-scroll-mt-20 tw-pb-14 sm:tw-pb-20">
            <SectionHeading eyebrow={chapterEyebrow('costs')}>
              What it costs.
            </SectionHeading>
            <BudgetBoard
              locationDetails={locationDetails}
              hotelLink={hotelUrl}
              rentalCarLink={rentalCarLink}
              busLink={busLink}
              privateDriverLink={privateDriverLink}
              taxiLink={taxiLink}
              toursLink={toursLink}
            />
          </section>
        )}

        {/* First nudge — after the visitor has received real value. The
            promo brings its own tw-my-12; don't stack more spacing on it. */}
        <div className="tw-pb-14 sm:tw-pb-20 [&_.tf-inline-promo]:tw-my-0">
          <SectionDivider className="tw-pb-10" />
          <InlineBlogCta
            countryCode={locationDetails.countryCode}
            thumbUrl={locationDetails.image}
          />
        </div>

        {/* Best experiences */}
        {locationDetails.topThingsToDo &&
          locationDetails.topThingsToDo.length > 0 && (
            <section
              id="experiences"
              className="tw-scroll-mt-20 tw-pb-14 sm:tw-pb-20"
            >
              <SectionHeading eyebrow={chapterEyebrow('experiences')}>
                Best experiences.
              </SectionHeading>
              <ThingsToDoList
                topThingsToDo={locationDetails.topThingsToDo}
                funFact={locationDetails.funFact}
              />
            </section>
          )}
      </div>

      {/* Full-bleed photo moment — air between dense chapters */}
      {images.length > 0 && <DestinationImage image={images[0]} fullBleed />}

      {/* At the table — tinted band; the dish menu is the star, the app
          note a quiet aside beside it. */}
      {locationDetails.mustTryDishes &&
        locationDetails.mustTryDishes.length > 0 && (
          <section
            id="food"
            className="tw-scroll-mt-20 tw-bg-[#F3EFE4] dark:tw-bg-[#13211B]"
          >
            <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-py-14 sm:tw-py-20">
              <SectionHeading eyebrow={chapterEyebrow('food')}>
                Eat like you live here.
              </SectionHeading>
              <div className="tw-grid tw-items-center tw-gap-10 lg:tw-grid-cols-[minmax(0,26rem)_1fr]">
                <div className="-tw-rotate-[0.8deg]">
                  <DishMenuCard
                    mustTryDishes={locationDetails.mustTryDishes}
                    title={locationDetails.title}
                  />
                </div>
                <div className="tw-flex lg:tw-justify-center">
                  <GuideAppStrip />
                </div>
              </div>
            </div>
          </section>
        )}

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4 tw-pt-14 sm:tw-pt-20">
        {/* At the border: customs declaration and FAQ share one spread */}
        {(hasEtiquette || hasFaq) && (
          <section className="tw-pb-14 sm:tw-pb-20">
            <SectionHeading eyebrow="Before you go">
              At the border.
            </SectionHeading>
            <div
              className={
                hasEtiquette && hasFaq
                  ? 'tw-grid tw-items-start tw-gap-8 lg:tw-grid-cols-2'
                  : ''
              }
            >
              <CustomsCard travelEtiquette={locationDetails.travelEtiquette} />
              <GuideFaq
                faq={locationDetails.faq}
                rentalCarLink={rentalCarLink}
                busLink={busLink}
                privateDriverLink={privateDriverLink}
                taxiLink={taxiLink}
                toursLink={toursLink}
              />
            </div>
          </section>
        )}

        {/* Itineraries */}
        {hasItineraries && (
          <section
            id="itineraries"
            className="tw-scroll-mt-20 tw-pb-14 sm:tw-pb-20"
          >
            <SectionHeading eyebrow={chapterEyebrow('itineraries')}>
              Itineraries.
            </SectionHeading>
            <RouteItinerary
              itineraries={itineraries}
              locationDetails={locationDetails}
            />
          </section>
        )}
      </div>

      {/* Full-bleed photo moment #2 */}
      {images.length > 1 && (
        <div className="tw-pb-14 sm:tw-pb-20">
          <DestinationImage image={images[1]} fullBleed />
        </div>
      )}

      {/* Go deeper — postcard shelf escapes the container for breathing room */}
      {hasSubdivisions && (
        <section
          id="regions"
          className="tw-mx-auto tw-max-w-5xl tw-scroll-mt-20 tw-px-4 tw-pb-14 sm:tw-pb-20"
        >
          <SectionHeading eyebrow={chapterEyebrow('regions')}>
            Where to in {locationDetails.title}.
          </SectionHeading>
          <SubdivisionGrid countryCode={locationDetails.countryCode} />
        </section>
      )}

      <div className="tw-mx-auto tw-max-w-5xl tw-px-4">
        {/* Community stories LAST — the grid scrolls forever, so nothing may
            come after it; the signup ask is woven into the grid instead. */}
        <section id="stories" className="tw-scroll-mt-20 tw-pb-16 sm:tw-pb-24">
          <SectionHeading eyebrow={chapterEyebrow('stories')}>
            {locationDetails.title} Travel Blogs
          </SectionHeading>
          <div className="tw-mb-10 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-shadow-[0_24px_50px_-30px_rgba(29,45,40,0.4)]">
            <div className="tw-flex tw-items-baseline tw-justify-between tw-px-5 tw-py-3 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.25em] tw-text-[#8A958F]">
              <span>THE MAP OF STORIES</span>
              <span className="tw-hidden sm:tw-inline">
                {locationDetails.title.toUpperCase()}
              </span>
            </div>
            <CountryMapPosts
              bbox={locationDetails.bbox}
              countryCode={locationDetails.countryCode}
            />
          </div>
          <GuidePostGrid
            query={query}
            about={locationDetails.title}
            cta={
              <StartBlogCta
                country={locationDetails.title}
                thumbUrl={locationDetails.image}
              />
            }
          />
        </section>
      </div>
    </>
  );
}
